import moment from 'moment';
import * as _ from 'underscore';
import { IObject } from '@type_definitions/datatypes';
import { Base64 } from 'js-base64';
import { isObservable } from 'rxjs';
import { TemplateRef } from '@angular/core';
import { AbstractControl } from '@angular/forms';

interface IUtil {
  isUndefined:(obj:any) => boolean;
  isNullOrUndefined:(obj:any) => boolean;
  isNaNOrNullOrUndefined:(obj:any) => boolean;
  isNullOrUndefinedOrEmpty:(obj:any) => boolean;
  notNullOrUndefined:(obj:any) => boolean;
  notNullOrUndefinedOrEmpty:(obj:any) => boolean;
  stringStartsWith:Function;
  capitalize:Function;
  noop:any;
  setValue:Function;
  getValue:Function;
  extractId:(object:IObject|number) => number;
  extractValue:(value?:any) => any;
  getDuplicatesFromArray:Function;
  decimals:Function;
  sortArray:Function;
  selectUniqueProperties:Function;
  currentTimeMillis:Function;
  urlWithoutQS:Function;
  applyOrGet:Function;
  copy:(source:any, target?:any, maxDepth?:number) => any;
  copyAndExtend:(...objs:any[]) => any;
  find:Function;
  indexOf:Function;
  remove:Function;
  elementToString:Function;
  replaceElementWithChild:Function;
  deepClone:<T = IObject>(source?:T) => any;
  deepCloneWithSanitization:<T = IObject>(source?:T) => any;
  isObjectEmpty:Function;
  diff:Function;
  compareArrayOrder:Function;
  isPromise:Function;
  sequentialPromise:Function;
  spread:(...args:any[]) => any;
  mergeRejected:Function;
  findWithSelf:Function;
  stringContains:Function;
  _keyStr:string;
  atob:Function;
  btoa:Function;
  tryNum: (obj:any) => number|string;
  convertPropValueToNumber:Function;
  csvToArray:Function;
  watchFirst:Function;
  stringToBoolean:Function;
  toJson:Function;
  fromJson:Function;
  regexEscape:Function;
  flipObject:Function;
  blobToBase64:Function;
  toCamelCase:Function;
  messagesToPromise:Function;
  splitLinks:(href:string) => string[];
  join:Function;
  reverseDeepMerge(dest:any, ...args:any[]):any;
  processLabel(label:string):string;
}

interface IUrlUtil {
  removeQueryString:Function;
  getQueryParams:(url:string) => IObject;
  encodeUri:Function;
  paramsToQueryString:Function;
  setQueryParams:Function;
  addQueryParams:Function;
  withoutOrigin:Function;
  replaceParams:Function;
  createUrlQueryParams:(params:IObject) => string;
}

interface IDiffUtil {
  value:Function;
  object:Function;
  compareValues:Function;
  arrayWithId:Function;
  createPatch:Function;
  compareArrayOfObjects:Function;
}

interface ICSSUtil {
  sumHeights:Function;
}

export enum PromiseSettledTypes {
  REJECTED = 'rejected',
  FULFILLED = 'fulfilled'
}

export const util:IUtil = <any>{};
window[ 'util' ] = util;
/**
 * Returns true if the given object is null or undefined
 */
util.isUndefined = _.isUndefined;

util.isNullOrUndefined =
  function(obj:any):boolean {
    return typeof obj === 'undefined' || obj === null;
  };

util.isNaNOrNullOrUndefined =
  function(obj:any):boolean {
    return typeof obj === 'undefined' || obj === null || _.isNaN(obj);
  };

util.isNullOrUndefinedOrEmpty =
  function(obj:any):boolean {
    return typeof obj === 'undefined' || obj === null || obj === '' || (_.isObject(obj) && jQuery.isEmptyObject(obj) && !(obj instanceof Date));
  };

util.notNullOrUndefined =
  function(obj:any):boolean {
    return !util.isNullOrUndefined(obj);
  };

util.notNullOrUndefinedOrEmpty =
  function(obj:any):boolean {
    return !util.isNullOrUndefinedOrEmpty(obj);
  };

util.stringStartsWith =
  function(str:string, search:string):boolean {
    return str.lastIndexOf(search, 0) === 0;
  };

util.capitalize =
  function capitalize(str:string):string {
    return str? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
  };

/**
 * A function that does nothing, useful for instance in promises; eg: promise.catch(util.noop)
 */
util.noop = () => {/***/
};

util.setValue =
  function(obj:any, keyString:string, value:any, skipUndefinedAndEmpty:boolean) {
    //check parameters
    if (this.isNullOrUndefined(obj) || this.isNullOrUndefined(keyString)) {
      return;
    }
    if (skipUndefinedAndEmpty) {
      if (this.isUndefined(value)) {
        return;
      }
      if (_.isArray(value) && value.length === 0) {
        return;
      }
      if (_.isObject(value) && jQuery.isEmptyObject(value)) {
        return;
      }
    }

    const keys = keyString.split('.');
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[ i ];
      if (this.isNullOrUndefined(obj[ key ])) {
        obj[ key ] = {};
      }
      obj = obj[ key ];
    }
    obj[ keys[ keys.length - 1 ] ] = value;
  };

util.getValue =
  function(obj:any, keyString:string):any {
    //check parameters
    if (this.isNullOrUndefined(obj) || this.isNullOrUndefined(keyString)) {
      return undefined;
    }

    const keys = keyString.split('.');
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[ i ];
      if (this.isNullOrUndefined(obj[ key ])) {
        return undefined;
      }
      obj = obj[ key ];
    }
    return obj[ keys[ keys.length - 1 ] ];
  };

util.extractId = (object:IObject|number):number => {
  return object && _.isObject(object)? (<IObject>object).id : object;
};

// extract the right value from the input:
// if an object is given return the value of the object key 'value', otherwise return the given value
util.extractValue = (value?:any):string => {
  return value && _.isObject(value)? value.value : value;
};

util.getDuplicatesFromArray = function(array) {
  const uniq = array
    .map((item) => {
      return {
        count: 1,
        item: item
      };
    })
    .reduce((a, b) => {
      a[ b.item ] = (a[ b.item ] || 0) + b.count;
      return a;
    }, {});

  return Object.keys(uniq).filter((a) => uniq[ a ] > 1);
};

//checks number of decimal places in a number
util.decimals = function(num:any):number {
  if (typeof num !== 'string') {
    num = num + '';
  }
  const pieces = num.split('.');
  if (pieces[ 1 ]) {
    return pieces[ 1 ].length;
  }
  return 0;
};

//sort an array by a list of dotted keyStrings
util.sortArray = function(array:any[]):any[] {
  // eslint-disable-next-line prefer-rest-params
  const args = arguments;
  array.sort((a, b) => {
    for (let i = 1; i < args.length; i++) {
      const keyString = args[ i ];
      const propA = util.getValue(a, keyString);
      const propB = util.getValue(b, keyString);
      if (propA > propB) {
        return 1;
      } else if (propA < propB) {
        return -1;
      }
    }
    return 0;
  });
  return array;
};

//selects a list of properties out of an object array (sorted)
util.selectUniqueProperties = function(array:any[], keyString:string):any {
  util.sortArray(array, keyString);
  const result = [];
  let prev = '';

  for (let i = 0; i < array.length; i++) {
    const prop = util.getValue(array[ i ], keyString);
    if (prop !== prev) {
      result.push(prop);
      prev = prop;
    }
  }

  return result;
};

util.currentTimeMillis =
  function() {
    return moment().valueOf();
  };

util.urlWithoutQS = function(url) {
  if (util.isNullOrUndefined(url)) {
    return url;
  }

  const indexOfQS = url.indexOf('?');
  if (indexOfQS >= 0) {
    return url.substring(0, indexOfQS);
  }
  return url;
};

util.applyOrGet = function(fnOrValue:any):any {
  return _.isFunction(fnOrValue)? fnOrValue() : fnOrValue;
};

util.copy = (data:any, objMap?:WeakMap<any, any>) => {
  if (!objMap) {
    // Map for handle recursive objects
    objMap = new WeakMap();
  }

  // recursion wrapper
  const deeper = (value:Object) => {
    if (value && typeof value === 'object') {
      return util.copy(value, objMap);
    }
    return value;
  };

  // Array value
  if (Array.isArray(data)) return data.map(deeper);

  // Object value
  if (data && typeof data === 'object') {
    // Same object seen earlier
    if (objMap.has(data)) return objMap.get(data);
    // Date object
    if (data instanceof Date) {
      const result:Date = new Date(data.valueOf());
      objMap.set(data, result);
      return result;
    }
    // Use original prototype
    const node = Object.create(Object.getPrototypeOf(data));
    // Save object to map before recursion
    objMap.set(data, node);
    for (const [key, value] of Object.entries(data)) {
      node[key] = deeper(value);
    }
    return node;
  }
  // Scalar value
  return data;
};

util.copyAndExtend = function(...objs:any[]):IObject {
  const args = Array.prototype.slice.call(objs);
  args[ 0 ] = util.copy(args[0]);
  return _.extend.apply(null, args);
};

//find an index in an array by using a 'truth function'
util.find = function(array, filterFn) {
  for (let i = 0; i < array.length; i++) {
    if (filterFn(array[ i ], i, array)) {
      return i;
    }
  }
  return -1;
};

//find an index in an array by comparing an object (using angular.equals), or by using a 'truth function'
util.indexOf = function(array:any[], fnOrValue:any):number {
  if (_.isFunction(fnOrValue)) {
    return _.indexOf(array, _.find(array, fnOrValue));
  } else {
    return _.indexOf(array, _.find(array, (v) => {
      return _.isEqual(v, fnOrValue);
    }));
  }
};

//removes elements from an array
util.remove = function(array:any[]) {
  // eslint-disable-next-line prefer-rest-params
  for (let i = 1; i < arguments.length; i++) {
    // eslint-disable-next-line prefer-rest-params
    const idx = _.indexOf(array, arguments[ i ]);
    if (idx >= 0) {
      array.splice(idx, 1);
    }
  }
};

util.elementToString = function(element) {
  if (util.isNullOrUndefined(element)) {
    return element;
  }

  if (element instanceof jQuery) {
    return $('<div>').append((<JQuery>element).clone()).html();
  } else if (element.outerHTML) {
    return element.outerHTML;
  }

  return element.toString();
};

util.replaceElementWithChild = function(element:JQuery<HTMLElement>):void {
  if (!element.replaceWith) {
    element = $(element);
  }
  const child = $(element[ 0 ].firstChild);
  mergeAttributes(element, child);
  element.replaceWith(child);

  function mergeAttributes(sourceElm, targetElm) {
    const attributes = sourceElm[ 0 ].attributes;
    for (let i = 0; i < attributes.length; i++) {
      const item = attributes[ i ];
      if (!item.specified) {
        continue;
      }

      const key = item.name;
      const sourceVal = item.value;
      const targetVal = targetElm.attr(key);

      if (sourceVal === targetVal) {
        continue;
      }

      const newVal = targetVal === undefined? sourceVal : sourceVal + ' ' + targetVal;

      targetElm.attr(key, newVal);
    }
  }
};

// fast but it only copies strings/numbers/plain-objects/nulls; it doesn't copy functions, deep objects
util.deepClone = function<T = IObject>(obj:T):IObject {
  if (!obj && !_.isObject(obj)) {
    return obj;
  }

  return JSON.parse(JSON.stringify(obj));
};

/**
 * Deeply clones an object, preserving data types (e.g., `Date`) and removing `undefined` properties.
 *
 * @template T - The type of the input object.
 * @param {T} input - The object to clone.
 * @returns {IObject} - A sanitized deep clone of the input object.
 */
util.deepCloneWithSanitization = function<T = IObject>(input:T):any {
  if (input === null || typeof input !== 'object') {
    // Return primitive values and functions as they are
    return input;
  }

  if (input instanceof Date) {
    // Handle Date objects
    return new Date(input.getTime());
  }

  if (Array.isArray(input)) {
    // Handle arrays
    return input
      .filter((item) => item !== undefined) // Remove undefined items
      .map((item) => util.deepCloneWithSanitization(item)); // Recursively deep copy
  }

  // Handle plain objects
  const result:IObject = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) {
      result[key] = util.deepCloneWithSanitization(value); // Recursively deep copy
    }
  }

  return result;
};

// copied from https://github.com/ngx-formly/ngx-formly/blob/main/src/core/src/lib/utils.ts
util.reverseDeepMerge = (dest:any, ...args:any[]):any => {
  const objAndSameType = (obj1: any, obj2: any):boolean => {
    return (
      _.isObject(obj1) &&
      _.isObject(obj2) &&
      Object.getPrototypeOf(obj1) === Object.getPrototypeOf(obj2) &&
      !(Array.isArray(obj1) || Array.isArray(obj2))
    );
  };

  const clone = (value:any):any => {
    if (
      !_.isObject(value) ||
      isObservable(value) ||
      value instanceof TemplateRef ||
      /* instanceof SafeHtmlImpl */ value.changingThisBreaksApplicationSecurity ||
      ['RegExp', 'FileList', 'File', 'Blob'].indexOf(value.constructor.name) !== -1
    ) {
      return value;
    }

    if (value instanceof Set) {
      return new Set(value);
    }

    if (value instanceof Map) {
      return new Map(value);
    }

    if (value instanceof Uint8Array) {
      return new Uint8Array(value);
    }

    if (value instanceof Uint16Array) {
      return new Uint16Array(value);
    }

    if (value instanceof Uint32Array) {
      return new Uint32Array(value);
    }

    // https://github.com/moment/moment/blob/master/moment.js#L252
    if (value._isAMomentObject && _.isFunction(value.clone)) {
      return value.clone();
    }

    if (value instanceof AbstractControl) {
      return null;
    }

    if (value instanceof Date) {
      return new Date(value.getTime());
    }

    if (Array.isArray(value)) {
      return value.slice(0).map((v) => clone(v));
    }

    // best way to clone a js object maybe
    // https://stackoverflow.com/questions/41474986/how-to-clone-a-javascript-es6-class-instance
    const proto = Object.getPrototypeOf(value);
    let c = Object.create(proto);
    c = Object.setPrototypeOf(c, proto);
    // need to make a deep copy so we don't use Object.assign
    // also Object.assign won't copy property descriptor exactly
    return Object.keys(value).reduce((newVal, prop) => {
      const propDesc = Object.getOwnPropertyDescriptor(value, prop);
      if (propDesc.get) {
        Object.defineProperty(newVal, prop, propDesc);
      } else {
        newVal[prop] = clone(value[prop]);
      }

      return newVal;
    }, c);
  };

  args.forEach((src) => {
    for (const srcArg in src) {
      if (dest[srcArg] === null || dest[srcArg] === '') {
        dest[srcArg] = clone(src[srcArg]);
      } else if (objAndSameType(dest[srcArg], src[srcArg])) {
        util.reverseDeepMerge(dest[srcArg], src[srcArg]);
      }
    }
  });

  return dest;
};

util.processLabel = (label:string):string => {
  return label ? label.replace(/[^\w\-]/g, '-').replace(/\-+/g, '-').toLowerCase() : label;
};

// check if an object has all empty values
util.isObjectEmpty = function(obj):boolean {
  return _.every(obj, (val:any) => {
    return util.isNullOrUndefinedOrEmpty(val);
  });
};

// make an object that contains all 'props' that are different in 'b' (compared to 'a')
util.diff = function(a, b, props) {
  if (!a || !b) {
    return undefined;
  }

  const result = {};

  //if we have a list of properties, compare those
  if (props) {
    _.forEach(props, (prop) => {
      const aProp = util.getValue(a, prop);
      const bProp = util.getValue(b, prop);
      if (!eq(aProp, bProp)) {
        set(result, prop, bProp);
      }
    });
  } else {
    //add all that are different
    _.forEach(a, (aProp, prop) => {
      const bProp = b[ prop ];
      if (!eq(aProp, bProp)) {
        set(result, prop, bProp);
      }
    });

    //add all that have been added
    _.forEach(b, (bProp, prop) => {
      // eslint-disable-next-line no-prototype-builtins
      if (!a.hasOwnProperty(prop)) {
        set(result, prop, bProp);
      }
    });
  }

  return result;

  function set(obj, key, value) {
    util.setValue(obj, key, _.isUndefined(value)? null : value);
  }

  function eq(a, b) {
    //shortcut for objects that have an id
    return (a && a.id && b && b.id && a.id === b.id) || _.isEqual(a, b);
  }
};


// when you change the order of the items in an array and you compare the original array and the new one, this returns the new order
// example: when comparing ['a', 'b', 'c'] and ['a', 'c', 'b'], you get [0, 2, 1]
util.compareArrayOrder = function(array1:any[], array2:any[]):any {
  const result = [];

  if (_.isEqual(array1, array2)) {
    return false;
  }

  _.forEach(array2, (val2) => {
    const resultKey = _.findIndex(array1, (val1) => {

      return _.isEqual(val1, val2);
    });
    result.push(resultKey);
  });
  return result;
};


util.isPromise = function(o:any):boolean {
  return util.notNullOrUndefined(o) && !_.isUndefined(o.then);
};

// multiple executions in sequential order of the same promise (waiting for first promise to be resolved before calling the second promise); reference: https://css-tricks.com/why-using-reduce-to-sequentially-resolve-promises-works/
// promiseFn: a function that returns a promise | ids: an array of idx that will be passed to the promise
// returns a promise after all promises are resolved
util.sequentialPromise = (promiseFn:(idx:number) => Promise<any>, ids:number[]):Promise<any> => {
  return ids.reduce(
    (accumulatorPromise:Promise<any>, idx:number) => {
      return accumulatorPromise.then(() => {
        return promiseFn(idx);
      });
    }, Promise.resolve()
  );
};

//split a single array argument into separate arguments and pass them on to targetFn
//use thisArgInTargetFn to provide a 'this' in the context of the targetFn
util.spread = (targetFn:Function, thisArgInTargetFn:any) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return function(a, b) { //we need to define at least 2 parameters, or the $watchCollection listener won't see old values
    let params = [];
    // eslint-disable-next-line prefer-rest-params
    for (let i = 0; i < arguments.length; i++) {
      // eslint-disable-next-line prefer-rest-params
      params = params.concat(arguments[ i ]);
    }
    return targetFn.apply(thisArgInTargetFn, params);
  };
};

//mergeRejected collects all rejected reasons and merge them into an array
//useful for Promise.allSettled, for instance to collect all validation errors
util.mergeRejected = (targetFn:Function):Function => {
  return (settledResult:[PromiseSettledResult<any>, PromiseSettledResult<any>]):() => string[] => {
    const rejectedReasons:string[] = [];
    _.chain(settledResult)
      .filter((result:IObject) => result.status === PromiseSettledTypes.REJECTED)
      .pluck('reason')
      .each((errors:string[]) => {
        _.each(errors, (error:string) => {
          rejectedReasons.push(error);
        });
      });
    return targetFn(rejectedReasons);
  };
};

util.findWithSelf = function(element:any, selector) {
  if (element) {
    if (element.is(selector)) {
      return element;
    }
    return element.find(selector);
  }
};

util.currentTimeMillis = function() {
  if (!Date.now) {
    return new Date().getTime();
  }
  return Date.now();
};

util.stringContains = function(string, it) {
  return string.indexOf(it) !== -1;
};

util.atob = function(input) {
  // this decoder accepts both URL safe and non-URL safe base64 encoded strings
  return Base64.decode(input);
};

util.btoa = function(input) {
  // encode to URL safe base64 (where + is replaced by - and / is replaced by _ and padding is removed)
  return Base64.encodeURI(input);
};

util.tryNum = (s:any):number|string => {
  const isCSV:boolean = typeof s === 'string' && s.indexOf(',') !== -1; // do not take it as number when is a comma separated string
  const n:number = parseFloat(s);
  return isNaN(n) || isCSV ? s : n;
};

util.convertPropValueToNumber = (collection:IObject|any[]):IObject|any[] => {
  if(!(collection && (_.isObject(collection) || _.isArray(collection)))){
    return;
  }

  const nums:IObject = {};
  _.each(collection, (val:string, key:string) => {
    nums[key] = util.tryNum(val);
  });

  if(_.isArray(collection)){
    return Object.values(nums);
  }

  return nums;
};

// comma separated values to array of numbers (if possible)
util.csvToArray = (value:number|string) => {
  if(_.isNumber(value)){
    return [value];
  }
  const values:number[]|string[] = value?.split(',');
  return util.convertPropValueToNumber(values);
};

util.stringToBoolean = function(s):boolean {
  if (typeof s === 'string') {
    s = s.toLowerCase();
  }

  if (s === 'true' || s === 'yes' || s === true) {
    return true;
  }
  if (s === 'false' || s === 'no' || s === false) {
    return false;
  }
  return undefined;
};

util.toJson = function toJson(obj) {
  //remove all keys starting with '$'
  return JSON.stringify(obj, (key, value) => {
    return _.isString(key) && key.charAt(0) === '$'? undefined : value;
  });
};

util.fromJson = (obj:any) => {
  return _.isString(obj)? JSON.parse(obj) : obj;
};

// escape all characters that are reserved in a regex
util.regexEscape = (s:string):string => {
  if (util.isNullOrUndefined(s)) {
    return s;
  }

  const reserved:string[] = [
    '\\',
    '^',
    '(',
    ')',
    '[',
    '.',
    '?',
    '*',
    '{',
    '$',
    '+',
    '|'
  ];
  for (const r of reserved) {
    s = s.replace(new RegExp('\\' + r, 'g'), '\\' + r);
  }

  return s;
};

// flip key-value for an object
util.flipObject = (obj:{ [ key:string ]:string }):{ [ key:string ]:string } => {
  const o = {};
  _.each(obj, (value:string, name:string) => {
    o[ value ] = name;
  });
  return o;
};

// convert blob to base64
util.blobToBase64 = (blob:Blob):Promise<string|ArrayBuffer> => {
  return new Promise((resolve) => {
    const reader:FileReader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

// convert any string to camelCase
util.toCamelCase = (str):string => {
  return str
    .replace(/\s(.)/g, (s) => s.toUpperCase())
    .replace(/\s/g, '')
    .replace(/^(.)/, (s) => s.toLowerCase());
};

util.messagesToPromise = (errors:string[]):Promise<void|string[]> => errors?.length ? Promise.reject(errors) : Promise.resolve();

util.splitLinks = (href:string):string[] => {
  // split either on <br/> or escaped <br/> (&lt;br/&gt;)
  const regExp = /(?:\s*<br\/>\s*)|(?:\s*&lt;br\/&gt;\s*)/g;
  return href.split(regExp);
};

// Joins a list of strings using a specified separator. An optional lastSeparator parameter can be provided to replace the last separator
// E.g. util.join(['a', 'b', 'c', 'd'], ', ', ' and ') -> a, b, c and d
util.join = (array:string[], separator:string, lastSeparator?:string):string|undefined => {
  if (!array?.length || !separator) {
    return '';
  }

  return lastSeparator ? `${array.slice(0, -1).join(separator)}${lastSeparator}${array.slice(-1)}` : array.join(separator);
};

/// Service Utilities ///
export const svcUtil:any = {};
window[ 'svcUtil' ] = svcUtil;

svcUtil.addDataProperties = function addDataProperties(service, aService, properties) {
  //create getter/setters
  _.forEach(properties,
    (prop:any) => {
      const key = _.isString(prop)? prop : prop.name;
      service[ key ] = _.isString(prop)? undefined : prop.value;

      aService[ key ] = function(value) {
        if (arguments.length) {
          service[ key ] = value;
        }
        return service[ key ];
      };
    });

  //clear -> reset the state
  aService.clear = function clear() {
    _.forEach(properties,
      (prop:any) => {
        const key = _.isString(prop)? prop : prop.name;
        service[ key ] = _.isString(prop)? undefined : prop.value;
      });
  };

  return aService;
};

/// URL Utilities ///
export const urlUtil:IUrlUtil = <any>{};
window[ 'urlUtil' ] = urlUtil;
urlUtil.removeQueryString = function(url:string):string {
  if (!url) {
    return url;
  }

  const uri:uri.URI = new URI(url);
  uri.query('');
  return uri.toString();
};

urlUtil.getQueryParams = function(url:string):any {
  if (!url) {
    return {};
  }

  const uri:uri.URI = new URI(url);
  return uri.search(true);
};

urlUtil.encodeUri = function(s) {
  //encode ' ' as '+' to match what angular does
  return encodeURIComponent(s).replace(/%20/g, '+');
};

urlUtil.paramsToQueryString = function(queryParams) {
  function keyValString(key, val) {
    if (util.isNullOrUndefined(val)) {
      return encodeURIComponent(key);
    }
    return [
      encodeURIComponent(key),
      encodeURIComponent(val)
    ].join('=');
  }

  const params = [];
  const keys = _.keys(queryParams).sort();
  for (let i = 0; i < keys.length; i++) {
    const key = keys[ i ];
    const value = queryParams[ key ];
    if (_.isArray(value)) {
      for (let j = 0; j < value.length; j++) {
        params.push(keyValString(key, value[ j ]));
      }
    } else {
      params.push(keyValString(key, value));
    }
  }

  return params.join('&');
};

urlUtil.setQueryParams = function(url:string, queryParams:any):string {
  const baseUrl:string = urlUtil.removeQueryString(url);
  if (!queryParams) {
    return baseUrl;
  }

  const uri:uri.URI = new URI(baseUrl);
  uri.search(queryParams);
  return uri.toString();
};

urlUtil.addQueryParams = function(url:string, queryParams:IObject) {
  const newParams = _.extend(urlUtil.getQueryParams(url), queryParams);
  return urlUtil.setQueryParams(url, newParams);
};

urlUtil.withoutOrigin = function(url:string):string {
  if (!url) {
    return url;
  }

  const uri = new URI(url);
  uri.origin('');
  return uri.toString();
};

urlUtil.replaceParams = function(url:string, params:IObject):string {
  _.each(params, (value:any, key:string) => {
    url = url.replace(new RegExp(`{${key}}`, 'gi'), value);
  });
  return url;
};

urlUtil.createUrlQueryParams = (params:IObject):string => {
  const urlSearchParams:URLSearchParams = new URLSearchParams();

  _.each(params, (value:any, key:string) => {
    // filtered out empty value
    if (util.notNullOrUndefinedOrEmpty(value)) {
      // if value is an array then add multiple parameter names
      if (_.isArray(value)) {
        _.each(value, (v:any) => {
          urlSearchParams.append(key, v);
        });
      } else {
        urlSearchParams.append(key, value);
      }
    }
  });

  // sort url params
  urlSearchParams.sort();

  return urlSearchParams.toString();
};

// diff utilities
export const diffUtil:IDiffUtil = <any>{};
window[ 'diffUtil' ] = diffUtil;

diffUtil.value = function(obj1:any, obj2:any) {
  const compareResult = diffUtil.compareValues(obj1, obj2);
  if (compareResult === 'updated') {
    return obj2;
  } else if (compareResult === 'created') {
    return obj2;
  } else if (compareResult === 'deleted') {
    return null;
  }
};

diffUtil.object = function(obj1:any, obj2:any, skip:any) {
  const diffResult = {};
  const diffUnchanged = {};
  const diffDeleted = [];

  _.forEach(obj1, (val1, key1) => {
    if (_.contains(skip, key1)) {
      return;
    }
    const val2 = obj2[ key1 ];
    const compareResult = diffUtil.compareValues(val1, val2);
    if (compareResult === 'updated') {
      diffResult[ key1 ] = val2;
    } else if (compareResult === 'deleted') {
      diffDeleted.push(key1);
    } else {
      diffUnchanged[ key1 ] = {};
    }
  });

  _.forEach(obj2, (val2, key2) => {
    if (_.contains(skip, key2)) {
      return;
    }
    const val1 = diffResult[ key2 ] || diffUnchanged[ key2 ];
    if (!val1) {
      diffResult[ key2 ] = val2;
    }
  });

  return {
    result: diffResult,
    deleted: diffDeleted
  };
};

diffUtil.arrayWithId = function(obj1, obj2, id) {
  const diffResult = [];
  const diffUnchanged = [];
  const diffDeleted = [];

  _.forEach(obj1, (val1) => {
    const valId = val1[ id ];
    const searchObj = {};
    searchObj[ id ] = valId;
    const val2 = _.findWhere(obj2, searchObj);
    const compareResult = diffUtil.compareValues(val1, val2);
    if (compareResult === 'updated') {
      diffResult.push(val2);
    } else if (compareResult === 'deleted') {
      diffDeleted.push(searchObj);
    } else {
      diffUnchanged.push(val1);
    }
  });

  _.forEach(obj2, (val2) => {
    const valId = val2[ id ];
    if (valId) {
      const searchObj = {};
      searchObj[ id ] = valId;
      const val1 = _.findWhere(diffResult, searchObj) || _.findWhere(diffUnchanged, searchObj);
      if (!val1) {
        diffResult.push(val2);
      }
    } else {
      diffResult.push(val2);
    }
  });

  return {
    result: diffResult,
    deleted: diffDeleted
  };
};

diffUtil.compareValues = function(value1:any, value2:any):string {
  if (_.isEqual(value1, value2)) {
    return 'unchanged';
  }
  if (typeof (value1) === 'undefined') {
    return 'created';
  }
  if (typeof (value2) === 'undefined') {
    return 'deleted';
  }
  return 'updated';
};

diffUtil.createPatch = function(patchData):any {
  const patch:any = {};

  _.forEach(patchData, (row:any) => {
    const path = row.path.split('/');
    path.shift();
    let obj:any = patch;
    let prevArg;
    _.forEach(path, (arg:any, index) => {
      if (prevArg) {
        if (!isNaN(arg)) {
          obj[ prevArg ] = obj[ prevArg ] || [];
        } else if (_.isString(arg)) {
          obj[ prevArg ] = obj[ prevArg ] || {};
        }
      }

      if (index === path.length - 1) {
        if (row.op === 'replace' || row.op === 'add') {
          if (!row.value || row.value === '') {
            row.value = null;
          }
          if (prevArg) {
            obj[ prevArg ][ arg ] = row.value;
          } else {
            obj[ arg ] = row.value;
          }
        }

        if (row.op === 'remove') {
          if (prevArg) {
            obj[ prevArg ][ arg ] = null;
          } else {
            obj[ arg ] = null;
          }
        }

      }
      obj = obj[ prevArg ] || obj;
      prevArg = arg;
    });
  });

  return patch;

};

// Compare 2 arrays of objects and return arr1 - arr2 (array of missing objects from arr2). Modified from https://stackoverflow.com/a/32966051
diffUtil.compareArrayOfObjects = (arr1, arr2, property) => {
  property = property || 'id';
  return arr1.filter((o1) => {
    return !arr2.some((o2) => {
      return o1[ property ] === o2[ property ];
    });
  });
};


// CSS utilities
export const cssUtil:ICSSUtil = <any>{};
window[ 'cssUtil' ] = cssUtil;

// calculate the total height by summing up the heights of all elements with the specified CSS classes
cssUtil.sumHeights = (definedClasses:string):number => {
  if(!definedClasses){
    return 0;
  }

  const heightElements:string[] = definedClasses.split(/\s*;\s*/);

  let sumHeights = 0;
  _.each(heightElements, (heightElement:string) => {
    let elementHeight:number;

    if (heightElement.match(/\d+px/)) {
      elementHeight = Number(heightElement.replace('px', ''));
    } else {
      // some elements if it's hidden still takes up space, so we only calculate spacing if the element is visible
      elementHeight = $(heightElement).is(':hidden') ? 0 : $(heightElement).outerHeight(true);
    }
    sumHeights += elementHeight;
  });

  return sumHeights;
};